const getApiUrl = () => {
	if (typeof window !== 'undefined') return window.ENV.API_URL
	return process.env.API_URL ?? ''
}

export interface Bbox {
	x1: number
	y1: number
	x2: number
	y2: number
}

export interface Sentence {
	id: string
	displayText: string
	ttsText: string
	type: string
	paragraph: string
	bbox?: Bbox
}

export interface Page {
	page: number
	text: string
	sentences: Sentence[]
}

// ── Cold-start handling ──────────────────────────────────────────────

export type ColdStartState = 'unknown' | 'waking' | 'ready'

let coldStartState: ColdStartState = 'unknown'
let wakePromise: Promise<void> | null = null
const coldStartListeners = new Set<(state: ColdStartState) => void>()

function setColdStart(state: ColdStartState) {
	coldStartState = state
	coldStartListeners.forEach((l) => l(state))
}

export function getColdStartState() {
	return coldStartState
}

export function onColdStartChange(cb: (state: ColdStartState) => void) {
	coldStartListeners.add(cb)
	return () => {
		coldStartListeners.delete(cb)
	}
}

/** Fire-and-forget: start waking the API without waiting. */
export function preWarmApi() {
	if (typeof window === 'undefined') return
	ensureApiReady().catch(() => {})
}

async function ensureApiReady(): Promise<void> {
	if (coldStartState === 'ready') return
	if (wakePromise) return wakePromise

	setColdStart('waking')
	wakePromise = (async () => {
		for (let i = 0; i < 120; i++) {
			try {
				const res = await fetch(`${getApiUrl()}/api/wake`)
				if (res.ok) {
					setColdStart('ready')
					wakePromise = null
					return
				}
				// 408 = still starting, keep polling
			} catch {
				// network error, keep polling
			}
			await new Promise((r) => setTimeout(r, 5000))
		}
		wakePromise = null
		throw new Error('API failed to start after 10 minutes')
	})()
	return wakePromise
}

/**
 * Fetch wrapper that detects cold-start 503s, triggers wake-up, and retries.
 */
async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
	try {
		const res = await fetch(url, init)

		if (res.status === 503) {
			const body = await res.clone().json().catch(() => null)
			if (body?.status === 'cold') {
				await ensureApiReady()
				return fetch(url, init)
			}
		}

		// Successful response means API is up
		if (res.ok && coldStartState !== 'ready') {
			setColdStart('ready')
		}

		return res
	} catch (err) {
		// Network error could mean API is down
		if (err instanceof TypeError && coldStartState !== 'ready') {
			await ensureApiReady()
			return fetch(url, init)
		}
		throw err
	}
}

// ── Public API ───────────────────────────────────────────────────────

export async function computeSha256(file: File): Promise<string> {
	const buffer = await file.arrayBuffer()
	const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
	return Array.from(new Uint8Array(hashBuffer))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('')
}

export async function uploadDocument(file: File, documentId: string, uploadUrl: string): Promise<{ doc_id: string }> {
	const makeInit = (): RequestInit => {
		const form = new FormData()
		form.append('file', file)
		form.append('document_id', documentId)
		form.append('upload_url', uploadUrl)
		return {
			method: 'POST',
			body: form,
		}
	}

	const res = await apiFetch(`${getApiUrl()}/api/upload`, makeInit())
	if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`)
	return res.json()
}

export async function retryUpload(documentId: string, pdfUrl: string, storageId: string): Promise<void> {
	const res = await apiFetch(`${getApiUrl()}/api/retry`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ document_id: documentId, pdf_url: pdfUrl, storage_id: storageId }),
	})
	if (!res.ok) throw new Error(`Retry failed: ${res.statusText}`)
}

export async function generateSpeech(text: string, voice: string, signal?: AbortSignal): Promise<Blob> {
	const res = await apiFetch(`${getApiUrl()}/api/tts/speech`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ text, voice }),
		signal,
	})
	if (!res.ok) throw new Error(`TTS failed: ${res.statusText}`)
	const blob = await res.blob()
	if (blob.size < 1000) {
		throw new Error(`TTS returned empty audio (${blob.size} bytes)`)
	}
	return blob
}
